import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ExtractedRecord } from "@/types";

import { absoluteIndexOf, absoluteIndicesOf } from "../utils/recordIndexing";
import { getRecordValidationErrors } from "../utils/recordUtils";

/**
 * Determines if a record should be auto-excluded because it contains
 * insufficient meaningful data (e.g., only a relationship field with no name/ID).
 */
export const shouldAutoExclude = (record: ExtractedRecord): boolean => {
  const fields = record.extracted_fields || [];
  const validationErrors = getRecordValidationErrors(record);

  // Auto-exclude if: has validation errors AND very few fields (1-2)
  if (validationErrors.length > 0 && fields.length <= 2) {
    return true;
  }

  return false;
};

interface UseRecordExclusionsArgs {
  /**
   * The transformation whose records the exclusions apply to. The exclusion
   * set RESETS when this changes (#6755 review): a reprocess-in-place swaps
   * the transformation without remounting the tab, and absolute indices are
   * only meaningful within one transformation's record set — index N in the
   * new run is a different record. Carrying the old set across would post
   * stale indices as `excluded_record_indices` against the new run: plausible
   * numbers, wrong rows, nothing on screen to reveal it. (The identity-keyed
   * Set this replaced failed OPEN here — exclusions silently vanished; index
   * keying fixed that but converted the failure to WRONG, so the reset has to
   * be explicit.)
   */
  transformationId: string | undefined;
  /** Records loaded so far, each carrying its absolute record_index. */
  records: ExtractedRecord[];
  /** Linked-record resolver — cascade partner for bulk exclude/restore. */
  getLinked: (record: ExtractedRecord) => ExtractedRecord[];
}

/**
 * Reviewer exclusion state, keyed on ABSOLUTE record_index (#6755).
 *
 * Was a Set<ExtractedRecord> tracked by object reference. Two problems, and
 * the second is the serious one:
 *
 *  1. Identity is fragile. Any re-fetch or windowed read produces fresh
 *     objects and every exclusion silently vanishes from the set.
 *  2. Deriving the payload from identity required scanning the full array
 *     for positions. Position is the absolute index only for a complete,
 *     gap-free set, so under a windowed fetch a reviewer excluding one row
 *     would have removed a DIFFERENT claim line, invisibly.
 *
 * The index is what the worker excludes on (`excludedSet[i]` in
 * document_loading/workflow.go), so keying state on it removes the
 * translation step rather than making it safer.
 */
export const useRecordExclusions = ({ transformationId, records, getLinked }: UseRecordExclusionsArgs) => {
  const [excludedIndices, setExcludedIndices] = useState<ReadonlySet<number>>(new Set());

  // Absolute indices already offered for auto-exclusion — see the effect below.
  const autoExcludeConsidered = useRef<Set<number>>(new Set());

  // Absolute indices the AUTO pass excluded, as opposed to the reviewer.
  //
  // State rather than a ref because a consumer renders from it (#6920): the
  // reprocess confirm has to name how many exclusions are the REVIEWER'S, and
  // `excludedIndices` alone cannot answer that — it is the union of both, so a
  // document with 37 junk rows and an untouched reviewer reads as 37 decisions
  // about to be lost. Auto-exclusions are deterministic and recomputed on the
  // new transformation, so nothing is lost when they go.
  const [autoExcludedIndices, setAutoExcludedIndices] = useState<ReadonlySet<number>>(new Set());

  // Reset on transformation change — see the transformationId docblock above.
  // Also clears the considered-set, so genuine junk in the new run's records
  // is offered for auto-exclusion again rather than being skipped because the
  // old run happened to use the same indices.
  useEffect(() => {
    setExcludedIndices(new Set());
    setAutoExcludedIndices(new Set());
    autoExcludeConsidered.current = new Set();
  }, [transformationId]);

  const isRecordExcluded = useCallback(
    (record: ExtractedRecord): boolean => {
      const index = absoluteIndexOf(record);
      return index !== undefined && excludedIndices.has(index);
    },
    [excludedIndices]
  );

  // Batch exclude / restore — invoked from the CompactTable selection toolbar,
  // the rail's errored-reason rows and the outside-policy rows (#7436).
  //
  // `cascade` (default true) walks linked records — excluding a claimant
  // excludes its claim lines and reimbursements — over the LOADED records
  // only; see getLinkedRecords and the toolbar disclosure (#6848 tracks
  // server-side linkage). Callers acting on CLAIM LINES pass cascade:false:
  // getLinkedRecords is symmetric on the claimant key, so from a line it
  // returns the claimant row and every sibling line, and "Exclude 2" on a
  // reason group took the claimant's whole file with it (#7436 walkthrough,
  // 2026-10-20). A line decision is about that line.
  const bulkExclude = useCallback(
    (selected: ExtractedRecord[], { cascade = true }: { cascade?: boolean } = {}) => {
      setExcludedIndices((prev) => {
        const next = new Set(prev);
        for (const index of absoluteIndicesOf(selected)) next.add(index);
        if (cascade) {
          for (const record of selected) {
            for (const index of absoluteIndicesOf(getLinked(record))) next.add(index);
          }
        }
        return next;
      });
    },
    [getLinked]
  );

  const bulkRestore = useCallback(
    (selected: ExtractedRecord[], { cascade = true }: { cascade?: boolean } = {}) => {
      setExcludedIndices((prev) => {
        const next = new Set(prev);
        for (const index of absoluteIndicesOf(selected)) next.delete(index);
        if (cascade) {
          for (const record of selected) {
            for (const index of absoluteIndicesOf(getLinked(record))) next.delete(index);
          }
        }
        return next;
      });
    },
    [getLinked]
  );

  // Auto-exclude junk records on initial load — records with insufficient
  // data (e.g. only a "Relationship: SELF" field and no name/ID). Lives in a
  // useEffect rather than useMemo: useMemo's body must be pure (React reserves
  // the right to drop or recompute memos) and a setState call inside one
  // would double-fire under strict mode or skip under concurrent rendering.
  // The setExcludedIndices updater is a pure function of `prev`, so re-running
  // when the dep changes is idempotent on already-excluded records.
  useEffect(() => {
    if (!records.length) return;

    // Only auto-exclude records not CONSIDERED before (#6755).
    //
    // This effect used to run once, because records arrived in a single
    // response. Now records grow a page at a time, so it re-runs — and an
    // add-only pass would re-exclude a junk record the reviewer had
    // deliberately restored, silently, every time they loaded another page.
    // The ref records what has been offered for auto-exclusion, so a restore
    // sticks. It is a ref rather than state because it must not itself trigger
    // a render. It resets with the exclusion state: on navigation (unmount)
    // and on a transformation change (the effect above).
    const fresh: number[] = [];
    for (const record of records) {
      const index = absoluteIndexOf(record);
      if (index === undefined || autoExcludeConsidered.current.has(index)) continue;
      autoExcludeConsidered.current.add(index);
      if (shouldAutoExclude(record)) fresh.push(index);
    }
    if (fresh.length === 0) return;

    setExcludedIndices((prev) => {
      const next = new Set(prev);
      for (const index of fresh) next.add(index);
      return next;
    });
    setAutoExcludedIndices((prev) => {
      const next = new Set(prev);
      for (const index of fresh) next.add(index);
      return next;
    });
  }, [records]);

  // Excluded record indices for the approval payload.
  //
  // A sort of the state itself. The previous implementation scanned the full
  // extracted_records array and emitted array positions — correct only while
  // the SPA held every record, in order, with none dropped (#6755).
  const excludedRecordIndices = useMemo(() => [...excludedIndices].sort((a, b) => a - b), [excludedIndices]);

  // How many records the REVIEWER has decided differently from the auto pass
  // (#6920). The symmetric difference, deliberately:
  //
  //   in excluded, not auto  -> the reviewer excluded it by hand
  //   in auto, not excluded  -> the reviewer RESTORED an auto-excluded record
  //
  // Both are real work and both are discarded by a reprocess, because the set
  // resets and the auto pass re-runs over the new records. Counting only the
  // first would under-report a reviewer who spent their time putting rows back
  // — the inverse of the over-reporting this replaces.
  //
  // Not `excludedIndices.size`: that is the union, so it counts machine
  // decisions as reviewer decisions.
  const reviewerDivergenceCount = useMemo(() => {
    let n = 0;
    for (const index of excludedIndices) if (!autoExcludedIndices.has(index)) n += 1;
    for (const index of autoExcludedIndices) if (!excludedIndices.has(index)) n += 1;
    return n;
  }, [excludedIndices, autoExcludedIndices]);

  return {
    excludedIndices,
    excludedRecordIndices,
    autoExcludedIndices,
    reviewerDivergenceCount,
    isRecordExcluded,
    bulkExclude,
    bulkRestore,
  };
};
